// Iter 86.15 / Phase E7 Sprint 1 — RolePermissionMatrix.jsx
//
// Route: /admin/roles/matrix
//
// THE big grid: every role x every permission. Click a cell to toggle a
// permission grant locally. Changes are STAGED until the operator clicks
// Save Changes (D3=b — Edit Mode + Save Bar + Diff Preview).
//
// Server-side guards:
//   - super_admin column is always read-only (cells disabled).
//   - System-role columns are NOT read-only here (operator can adjust
//     their permissions via the Matrix), matching the RoleForm policy.
//   - Save sends one PATCH per role whose permission set actually
//     changed. Skips no-op roles. Reports per-role success/failure.
//
// The diff preview, shown above the Save bar in edit mode, summarises:
//   - per role:   N added, M removed   with chip strips
//   - per permission family: groups touched
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft, Check, X, FloppyDisk, Pencil, Warning, MagnifyingGlass, ListChecks,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useAuth } from "../../contexts/AuthContext";


export default function RolePermissionMatrix() {
    const { user } = useAuth();
    const canWrite = user?.role === "super_admin";

    const [permissions, setPermissions] = useState([]);
    const [roles, setRoles] = useState([]);
    const [baseGrants, setBaseGrants] = useState({}); // saved server state
    const [draftGrants, setDraftGrants] = useState({}); // local edits
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");
    const [groupFilter, setGroupFilter] = useState("all");
    const [confirmOpen, setConfirmOpen] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/permission/matrix");
            setPermissions(r.data.permissions || []);
            setRoles(r.data.roles || []);
            const g = {};
            for (const code in r.data.grants) {
                g[code] = new Set(r.data.grants[code]);
            }
            setBaseGrants(g);
            setDraftGrants(Object.fromEntries(Object.entries(g).map(([k, v]) => [k, new Set(v)])));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    // ── filters ───────────────────────────────────────────────────
    const groupValues = useMemo(() => {
        return Array.from(new Set(permissions.map((p) => p.group))).sort();
    }, [permissions]);

    const filteredPerms = useMemo(() => {
        const s = search.trim().toLowerCase();
        return permissions.filter((p) => {
            if (groupFilter !== "all" && p.group !== groupFilter) return false;
            if (s && !`${p.code} ${p.label} ${p.description}`.toLowerCase().includes(s)) return false;
            return true;
        });
    }, [permissions, search, groupFilter]);

    // ── diff calculation ──────────────────────────────────────────
    const diff = useMemo(() => {
        const out = {};
        for (const r of roles) {
            const base = baseGrants[r.code] || new Set();
            const draft = draftGrants[r.code] || new Set();
            const added = [];
            const removed = [];
            draft.forEach((c) => { if (!base.has(c)) added.push(c); });
            base.forEach((c) => { if (!draft.has(c)) removed.push(c); });
            if (added.length || removed.length) {
                out[r.code] = { added: added.sort(), removed: removed.sort() };
            }
        }
        return out;
    }, [roles, baseGrants, draftGrants]);

    const hasChanges = Object.keys(diff).length > 0;

    // ── cell toggle ───────────────────────────────────────────────
    const toggle = (roleCode, permCode) => {
        if (roleCode === "super_admin") return; // D4: never editable
        setDraftGrants((prev) => {
            const cur = new Set(prev[roleCode] || []);
            if (cur.has(permCode)) cur.delete(permCode); else cur.add(permCode);
            return { ...prev, [roleCode]: cur };
        });
    };

    const discardChanges = () => {
        setDraftGrants(Object.fromEntries(Object.entries(baseGrants).map(([k, v]) => [k, new Set(v)])));
        setEditMode(false);
    };

    const save = async () => {
        setSaving(true);
        const errors = [];
        const successes = [];
        for (const roleCode of Object.keys(diff)) {
            try {
                await api.patch(`/admin/role/${roleCode}`, {
                    permissions: Array.from(draftGrants[roleCode]).sort(),
                });
                successes.push(roleCode);
            } catch (err) {
                errors.push({ role: roleCode, err });
            }
        }
        setSaving(false);
        setConfirmOpen(false);
        if (errors.length === 0) {
            toast.success(`Saved ${successes.length} role(s).`);
            setEditMode(false);
            await reload();
        } else if (successes.length === 0) {
            toast.error(`Failed to save: ${errors.map((e) => `${e.role} (${formatApiError(e.err)})`).join("; ")}`);
        } else {
            toast.warning(`Saved ${successes.length} role(s); ${errors.length} failed: ${errors.map((e) => e.role).join(", ")}`);
            await reload();
        }
    };

    if (loading) {
        return <div className="p-6 text-sm text-slate-500" data-testid="matrix-loading">Loading...</div>;
    }

    return (
        <div className="space-y-5" data-testid="permission-matrix-page">
            <PageHeader
                title="Permission Matrix"
                subtitle={`${roles.length} roles × ${permissions.length} permissions. Click a cell to toggle a grant. Changes stage until you save.`}
                breadcrumbs={[
                    { label: "Administration" },
                    { label: "Roles", to: "/admin/roles" },
                    { label: "Permission Matrix" },
                ]}
                actions={
                    <div className="flex items-center gap-2">
                        <Link to="/admin/roles">
                            <Button variant="outline" data-testid="back-to-roles-btn">
                                <ArrowLeft className="w-4 h-4 mr-2" /> Back
                            </Button>
                        </Link>
                        {canWrite && !editMode && (
                            <Button onClick={() => setEditMode(true)} data-testid="enter-edit-mode-btn">
                                <Pencil className="w-4 h-4 mr-2" /> Enter Edit Mode
                            </Button>
                        )}
                        {canWrite && editMode && (
                            <Button variant="outline" onClick={discardChanges} data-testid="discard-changes-btn" disabled={saving}>
                                <X className="w-4 h-4 mr-2" /> Discard
                            </Button>
                        )}
                    </div>
                }
            />

            {editMode && (
                <div className="bg-indigo-50 border border-indigo-300 text-indigo-800 rounded-lg px-4 py-3 text-sm flex items-start gap-2" data-testid="edit-mode-banner">
                    <Pencil className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="font-medium">Edit Mode — changes are staged.</div>
                        <div className="text-xs">Click cells to toggle grants. Review the diff at the bottom, then click <strong>Save Changes</strong>. super_admin column is locked.</div>
                    </div>
                </div>
            )}

            {/* Filter bar */}
            <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3">
                <div className="relative">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search permission code / label"
                        className="pl-9 w-80"
                        data-testid="matrix-search-input"
                    />
                </div>
                <div className="flex items-center gap-1 text-xs">
                    <span className="text-slate-500 mr-1">Group:</span>
                    <button
                        type="button"
                        onClick={() => setGroupFilter("all")}
                        data-testid="group-filter-all"
                        className={`px-3 py-1.5 rounded-md font-medium ${
                            groupFilter === "all" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                    >
                        all ({permissions.length})
                    </button>
                    {groupValues.map((g) => (
                        <button
                            key={g}
                            type="button"
                            onClick={() => setGroupFilter(g)}
                            data-testid={`group-filter-${g}`}
                            className={`px-3 py-1.5 rounded-md font-medium ${
                                groupFilter === g ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                        >
                            {g}
                        </button>
                    ))}
                </div>
            </div>

            {/* The matrix */}
            <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="matrix-table-wrapper">
                <table className="text-sm" data-testid="matrix-table">
                    <thead className="sticky top-0 bg-slate-50">
                        <tr>
                            <th className="text-left px-4 py-2.5 sticky left-0 bg-slate-50 z-10 min-w-[280px] border-r border-slate-200">
                                Permission
                            </th>
                            {roles.map((r) => (
                                <th
                                    key={r.code}
                                    className={`px-2 py-2.5 text-center text-xs font-medium ${
                                        r.code === "super_admin" ? "bg-amber-50 text-amber-800" : "text-slate-700"
                                    }`}
                                    title={r.code}
                                    data-testid={`matrix-col-${r.code}`}
                                >
                                    <div className="whitespace-nowrap">{r.name}</div>
                                    <div className="text-[10px] text-slate-400 font-mono mt-0.5">{r.code}</div>
                                    {r.code === "super_admin" && (
                                        <div className="text-[10px] text-amber-600 mt-0.5">locked</div>
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {filteredPerms.map((p) => (
                            <tr key={p.code} className="hover:bg-slate-50" data-testid={`matrix-row-${p.code}`}>
                                <td className="px-4 py-2 sticky left-0 bg-white border-r border-slate-100 z-10">
                                    <div className="font-medium text-sm text-slate-900">
                                        {p.label}
                                        {p.danger && (
                                            <span className="ml-1.5 inline-flex items-center px-1 py-0 bg-rose-100 text-rose-700 text-[10px] font-medium rounded">
                                                danger
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-slate-500 font-mono">{p.code}</div>
                                </td>
                                {roles.map((r) => {
                                    const draft = draftGrants[r.code] || new Set();
                                    const base = baseGrants[r.code] || new Set();
                                    const checkedDraft = draft.has(p.code);
                                    const checkedBase = base.has(p.code);
                                    const changed = checkedDraft !== checkedBase;
                                    const locked = r.code === "super_admin" || !editMode;
                                    return (
                                        <td
                                            key={r.code}
                                            className={`text-center px-2 py-1.5 ${
                                                changed
                                                    ? checkedDraft
                                                        ? "bg-emerald-50"
                                                        : "bg-rose-50"
                                                    : ""
                                            }`}
                                            data-testid={`matrix-cell-${p.code}-${r.code}`}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => !locked && toggle(r.code, p.code)}
                                                disabled={locked}
                                                title={locked ? "Locked" : `${checkedDraft ? "Remove" : "Grant"} ${p.code}`}
                                                className={`w-6 h-6 rounded transition-colors mx-auto flex items-center justify-center ${
                                                    checkedDraft
                                                        ? "bg-indigo-600 text-white hover:bg-indigo-700"
                                                        : "border border-slate-300 hover:bg-slate-100"
                                                } ${locked ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                                                data-testid={`matrix-toggle-${p.code}-${r.code}`}
                                            >
                                                {checkedDraft && <Check className="w-3.5 h-3.5" />}
                                            </button>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Diff preview + Save bar (D3=b) */}
            {editMode && hasChanges && (
                <div className="sticky bottom-0 bg-white border-t-4 border-indigo-500 -mx-6 px-6 py-4 shadow-2xl" data-testid="diff-save-bar">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                                <ListChecks className="w-5 h-5 text-indigo-600" />
                                <h3 className="font-semibold text-slate-900">
                                    Pending changes
                                    <span className="ml-2 text-xs font-mono text-slate-500">{Object.keys(diff).length} role(s)</span>
                                </h3>
                            </div>
                            <div className="space-y-1.5 max-h-32 overflow-y-auto" data-testid="diff-preview">
                                {Object.keys(diff).sort().map((rc) => (
                                    <div key={rc} className="flex items-baseline gap-2 text-xs">
                                        <span className="font-medium text-slate-700 min-w-[140px]" data-testid={`diff-role-${rc}`}>{rc}</span>
                                        {diff[rc].added.length > 0 && (
                                            <span className="text-emerald-700">
                                                +{diff[rc].added.length} <span className="font-mono">({diff[rc].added.join(", ")})</span>
                                            </span>
                                        )}
                                        {diff[rc].removed.length > 0 && (
                                            <span className="text-rose-700 ml-2">
                                                −{diff[rc].removed.length} <span className="font-mono">({diff[rc].removed.join(", ")})</span>
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <Button variant="outline" onClick={discardChanges} disabled={saving} data-testid="diff-discard-btn">
                                Discard
                            </Button>
                            <Button onClick={() => setConfirmOpen(true)} disabled={saving} data-testid="diff-save-btn">
                                <FloppyDisk className="w-4 h-4 mr-2" />
                                {saving ? "Saving..." : `Save Changes (${Object.keys(diff).length})`}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Final confirm modal */}
            {confirmOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="confirm-save-modal">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <div className="flex items-start gap-3 mb-4">
                            <Warning className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-slate-900 mb-1">Apply permission changes?</h3>
                                <p className="text-sm text-slate-600">
                                    You are about to apply changes to <strong>{Object.keys(diff).length}</strong> role(s). The new permissions take effect on the next API request (60s cache). Every change is recorded in the audit log.
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={saving} data-testid="confirm-cancel-btn">
                                Cancel
                            </Button>
                            <Button onClick={save} disabled={saving} data-testid="confirm-apply-btn">
                                {saving ? "Saving..." : "Yes, apply"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
